import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalDialog } from 'components/ModalDialog';
import type {
    BimActKc2OtherItemData,
    BimActKc2OtherTotalItemData,
    BimActKc2VersionOtherItemData,
    BimActKc2VersionOtherTotalItemData,
} from 'gql/graphql';
import { cnMixSpace, Loader } from 'uikit';
import { descriptionNumberAndDate } from 'utils/descriptionNumberAndDate';

import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';

import { useBimWorkOrderOtherVersionActDetailsData } from '../../WorkOrderVersion/BimWorkOrderOthersVersionTable/hooks/useBimWorkOrderOtherVersionActDetailsData';
import type { IActKc2OtherItem } from '../BimWorkOrderActsKc2List';

import { useBimWorkOrderOtherActDetailsData } from './hooks';

import Style from './BimWorkOrderOtherActKc2Details.module.css';

interface IProps {
    actKC2: BimActKc2OtherItemData | BimActKc2VersionOtherItemData | IActKc2OtherItem;
    onClose: () => void;
    styleClass: string;
    defaultColDef?: ColDef;
    isVersionPage?: boolean;
    isActionDisabled?: boolean;
}

const getRowId = ({ data }: { data: BimActKc2OtherTotalItemData }) =>
    data?.isInflation ? `${data?.id}-${data?.workOrderSeasonId}` : data?.id;

const getRowVersionId = ({ data }: { data: BimActKc2VersionOtherTotalItemData }) =>
    data?.isInflation ? `${data?.id}-${data?.workOrderVersionSeasonId}` : data?.id;

export const BimWorkOrderOtherActKc2Details = ({
    defaultColDef,
    styleClass,
    actKC2,
    onClose,
    isVersionPage,
    isActionDisabled = false,
}: IProps) => {
    const { detailsData, isLoading, detailsColumns, rowClassRules } = useBimWorkOrderOtherActDetailsData({
        actKC2Id: actKC2.id,
        isVersionPage,
        isActionDisabled,
        isInactive: !!actKC2.isInactive,
    });

    const { detailsVersionData, isLoadingVersion, detailsVersionColumns, rowClassVersionRules } =
        useBimWorkOrderOtherVersionActDetailsData({
            actKC2Id: actKC2.id,
        });

    const currentData = isVersionPage ? detailsVersionData : detailsData;
    const currentStatus = isVersionPage ? isLoadingVersion : isLoading;
    const currentColumns = isVersionPage ? detailsVersionColumns : detailsColumns;
    const currentRowClassRules = isVersionPage ? rowClassVersionRules : rowClassRules;
    const currentRowId = isVersionPage ? getRowVersionId : getRowId;

    const modalTitle = useMemo(
        () => `Итоги акта ${actKC2.number && descriptionNumberAndDate(actKC2.number, actKC2.date)}`,
        [actKC2.date, actKC2.number],
    );

    return (
        <ModalDialog
            isOpen
            title={modalTitle}
            onClose={onClose}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
        >
            <div className={`${cnMixSpace({ p: 's' })} ag-theme-alpine ${styleClass}`}>
                <AgGridTable
                    rowData={currentData}
                    columnDefs={currentColumns}
                    animateRows
                    suppressRowClickSelection
                    overlayNoRowsTemplate="Нет данных"
                    defaultColDef={defaultColDef}
                    rowClassRules={currentRowClassRules}
                    getRowId={currentRowId as any}
                />
                {currentStatus && (
                    <div className={Style.overlayLoader}>
                        <Loader />
                    </div>
                )}
            </div>
        </ModalDialog>
    );
};

BimWorkOrderOtherActKc2Details.displayName = 'BimWorkOrderOtherActKc2Details';
